import { brandPalette } from "./brandPalette";
import logo from "../assets/logo.png";
import logoLight from "../assets/logo-light.png";

const darkPalette = {
  primary: brandPalette.primary,
  secondary: brandPalette.secondary,
  tertiary: brandPalette.stone,
  neutralText: "rgba(247, 242, 236, 0.86)",
};

/** Light-mode sidebar tokens (beige / off-white chrome). */
export const adminSidebarColors = {
  background: brandPalette.sand,
  activePill: "#F2E8E1",
  activeText: brandPalette.primary,
  inactiveText: "#5A5550",
  sectionLabel: brandPalette.taupe,
  border: brandPalette.stone,
  footerBg: brandPalette.warmBeige,
};

export const adminSidebarShellProps = (isDark) =>
  isDark
    ? {
        bg: darkPalette.primary,
        borderRight: "1px solid",
        borderColor: darkPalette.tertiary,
      }
    : {
        bg: adminSidebarColors.background,
        borderRight: "1px solid",
        borderColor: adminSidebarColors.border,
      };

export const adminHeaderTitleProps = (isDark) =>
  isDark
    ? {
        fontSize: "xl",
        fontWeight: "800",
        letterSpacing: "tight",
        textTransform: "uppercase",
        color: "white",
      }
    : {
        fontSize: "xl",
        fontWeight: "800",
        letterSpacing: "tight",
        textTransform: "uppercase",
        color: brandPalette.primary,
      };

export const adminHeaderSubtitleProps = (isDark) => ({
  fontSize: "10px",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: isDark ? "rgba(255,255,255,0.6)" : adminSidebarColors.sectionLabel,
});

export const adminSidebarLogoProps = (isDark) => ({
  src: isDark ? logoLight : logo,
  alt: "Edutopia",
  w: "140px",
  h: "auto",
  cursor: "pointer",
  mx: "auto",
});

export const adminSectionLabelProps = (isDark) => ({
  px: 2,
  mb: 2,
  fontSize: "xs",
  color: isDark ? "rgba(255,255,255,0.5)" : adminSidebarColors.sectionLabel,
  textTransform: "uppercase",
  letterSpacing: "0.22em",
  fontWeight: "700",
  textAlign: "left",
});

export const adminNavItemProps = (active, isDark) => {
  const layout = {
    align: "center",
    justify: "flex-start",
    gap: 3,
    px: 3,
    py: 2,
    borderRadius: "14px",
    transition: "all 150ms ease",
    fontSize: "sm",
    fontWeight: "600",
  };

  if (isDark) {
    return {
      ...layout,
      bg: active ? darkPalette.secondary : "transparent",
      color: active ? "white" : darkPalette.neutralText,
      borderLeft: active ? "4px solid" : "4px solid transparent",
      borderColor: active ? darkPalette.tertiary : "transparent",
      _hover: {
        bg: active ? darkPalette.secondary : "rgba(69, 11, 17, 0.5)",
        color: "white",
      },
    };
  }

  return {
    ...layout,
    bg: active ? adminSidebarColors.activePill : "transparent",
    color: active ? adminSidebarColors.activeText : adminSidebarColors.inactiveText,
    border: active ? "1px solid rgba(91, 13, 22, 0.22)" : "1px solid transparent",
    borderLeft: active ? "4px solid" : "4px solid transparent",
    borderColor: active ? "rgba(91, 13, 22, 0.22)" : "transparent",
    borderLeftColor: active ? brandPalette.primary : "transparent",
    _hover: {
      bg: active ? adminSidebarColors.activePill : "rgba(91, 13, 22, 0.06)",
      color: active ? adminSidebarColors.activeText : brandPalette.primary,
      borderColor: active ? "rgba(91, 13, 22, 0.35)" : "transparent",
      borderLeftColor: active ? brandPalette.primary : "transparent",
    },
  };
};

export const adminFooterProps = (isDark) =>
  isDark
    ? { p: 4, bg: darkPalette.secondary }
    : {
        p: 4,
        bg: adminSidebarColors.footerBg,
        borderTop: "1px solid",
        borderColor: adminSidebarColors.border,
      };

export const adminFooterAvatarBoxProps = (isDark) => ({
  w: "40px",
  h: "40px",
  flexShrink: 0,
  bg: isDark ? darkPalette.primary : "var(--surface-raised)",
  border: "1px solid",
  borderColor: isDark ? darkPalette.tertiary : adminSidebarColors.border,
  align: "center",
  justify: "center",
  borderRadius: "12px",
});

export const adminFooterAvatarIconProps = (isDark) => ({
  color: isDark ? "white" : brandPalette.primary,
  fontSize: "20px",
});

export const adminFooterNameProps = (isDark) => ({
  color: isDark ? "white" : adminSidebarColors.inactiveText,
  fontSize: "sm",
  fontWeight: "700",
  noOfLines: 1,
});

export const adminFooterSettingsButtonProps = (isDark) => ({
  variant: "ghost",
  flexShrink: 0,
  borderRadius: "12px",
  color: isDark ? "white" : brandPalette.primary,
  _hover: { bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(91, 13, 22, 0.08)" },
  _active: { bg: isDark ? "rgba(255,255,255,0.18)" : "rgba(91, 13, 22, 0.14)" },
});

export const adminFooterControlProps = (isDark) => ({
  bell: {
    iconColor: isDark ? "white" : brandPalette.primary,
    hoverBg: isDark ? "rgba(255,255,255,0.12)" : "rgba(91, 13, 22, 0.08)",
  },
  themeToggle: {
    tone: isDark ? "onMaroon" : "onSurface",
  },
});

export const adminNavIconFilter = (active) =>
  active
    ? "brightness(0) saturate(100%) invert(11%) sepia(42%) saturate(4263%) hue-rotate(328deg) brightness(95%) contrast(101%)"
    : "brightness(0) saturate(100%) invert(37%) sepia(7%) saturate(748%) hue-rotate(349deg) brightness(94%) contrast(88%)";

export const adminNavIconImageProps = (active, isDark) =>
  isDark
    ? {
        w: "18px",
        h: "18px",
        alt: "",
        filter: "brightness(0) invert(1)",
        opacity: active ? 1 : 0.85,
      }
    : {
        w: "18px",
        h: "18px",
        alt: "",
        filter: adminNavIconFilter(active),
      };

export const adminMobileDrawerShellProps = (isDark) => ({
  maxW: "min(100vw, 300px)",
  bg: isDark ? darkPalette.secondary : adminSidebarColors.background,
  borderRightWidth: "0",
});

export const adminMobileDrawerHeaderProps = (isDark) => ({
  borderBottomWidth: "1px",
  borderColor: isDark ? "rgba(255,255,255,0.12)" : adminSidebarColors.border,
  py: 3,
  px: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 3,
  bg: isDark ? darkPalette.secondary : adminSidebarColors.background,
});

export const adminMobileDrawerTitleProps = (isDark) => ({
  fontSize: "md",
  fontWeight: "800",
  color: isDark ? brandPalette.cream : brandPalette.primary,
  letterSpacing: "0.04em",
});

export const adminMobileDrawerCloseButtonProps = (isDark) => ({
  size: "md",
  borderRadius: "full",
  variant: "ghost",
  color: isDark ? brandPalette.cream : brandPalette.primary,
  _hover: { bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(91, 13, 22, 0.08)" },
});

/** Popover chrome on the sidebar settings menu. */
export const adminSidebarPopoverProps = (isDark) => ({
  borderColor: isDark ? darkPalette.tertiary : adminSidebarColors.border,
});
